import {useMergeboxStatusCalculator} from '@github-ui/mergebox/use-mergebox-status-calculator'
import {EMPTY_CHANNELS} from '@github-ui/mergebox/use-mergeability-live-updates'
import {useMergeStatusButtonData} from '../../../../hooks/use-merge-status-button-data'
import {useLayoutRouteData} from '../../../../page-data/loaders/use-layout-route-data'

export type MergeBoxPullRequest = ReturnType<typeof useMergeboxStatusCalculator>['pullRequest']

/**
 * Wraps the merge-status calculator pipeline (which also wires up the relevant
 * live updates) and exposes the mergeability data that For You items derive
 * their state from.
 *
 * Returns `null` when there is no validated merge data, in which case consumers
 * should early return so the item is not registered as visible.
 */
export function useMergeabilityData() {
  const {pullRequest, mergeStatusButtonData} = useLayoutRouteData()
  const validatedMergeData = useMergeStatusButtonData(pullRequest.state, mergeStatusButtonData)

  // hack: the calculator hook was not built with undefined/null in mind
  const headSha = validatedMergeData?.headSha ?? ''
  const channels = validatedMergeData?.channels ?? EMPTY_CHANNELS

  const {
    calculator,
    pullRequest: mergeBoxPullRequest,
    statusChecksData,
    mergeRequirements,
  } = useMergeboxStatusCalculator({headSha, channels})

  if (!validatedMergeData) return null

  const userStateCondition = mergeRequirements?.conditions.find(
    condition => condition.type === 'PULL_REQUEST_USER_STATE',
  )
  const canUserPushToBase = userStateCondition?.result === 'PASSED'

  return {
    mergeBoxPullRequest,
    statusChecks: statusChecksData.statusChecks,
    statusRollup: statusChecksData.statusRollup,
    checksSection: calculator.sections.ChecksSection,
    conflictsSection: calculator.sections.ConflictsSection,
    blockedSection: calculator.sections.BlockedSection,
    canUserPushToBase,
  }
}

export type MergeabilityData = NonNullable<ReturnType<typeof useMergeabilityData>>
